import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { ApiClientError, api } from '../services/api';
import type { User } from '../types';

export type AuthState = 'loading' | 'authenticated' | 'anonymous';

export type AuthContextValue = {
  state: AuthState;
  user: User | null;
  error: string | null;
  sessionWarning: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (input: {
    currentPassword: string;
    newPassword: string;
    newPasswordConfirm: string;
  }) => Promise<void>;
  updateProfile: (update: {
    displayName?: string;
    avatarUrl?: string | null;
    styleProfile?: string | null;
  }) => Promise<void>;
  refresh: () => Promise<void>;
};

const WARNING_THRESHOLD_MS = 2 * 60 * 1_000;
const REFRESH_THRESHOLD_MS = 5 * 60 * 1_000;
const SESSION_CHECK_INTERVAL_MS = 30 * 1_000;

export const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AuthState>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionWarning, setSessionWarning] = useState<string | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null);
  const isRefreshingRef = useRef(false);

  const handleSessionExpired = useCallback(() => {
    setUser(null);
    setState('anonymous');
    setSessionExpiresAt(null);
    setSessionWarning(null);
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const result = await api.me();
      setUser(result.user);
      setSessionExpiresAt(result.expiresAt);
      setSessionWarning(null);
      setState('authenticated');
      setError(null);
    } catch (reason) {
      if (reason instanceof ApiClientError && reason.status === 401) {
        handleSessionExpired();
        return;
      }
      setUser(null);
      setState('anonymous');
      setSessionExpiresAt(null);
      setSessionWarning(null);
      setError('De serverstatus kon niet worden geladen.');
    }
  }, [handleSessionExpired]);

  useEffect(() => {
    const controller = new AbortController();
    void api
      .me(controller.signal)
      .then((result) => {
        setUser(result.user);
        setSessionExpiresAt(result.expiresAt);
        setSessionWarning(null);
        setState('authenticated');
        setError(null);
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setUser(null);
        setSessionExpiresAt(null);
        setSessionWarning(null);
        setState('anonymous');
        setError(
          reason instanceof ApiClientError && reason.status === 401
            ? null
            : 'De server is momenteel niet bereikbaar.',
        );
      });
    return () => controller.abort();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.login({ email, password });
    setUser(result.user);
    setSessionExpiresAt(result.expiresAt);
    setSessionWarning(null);
    setState('authenticated');
    setError(null);
  }, []);

  const register = useCallback(
    async (username: string, email: string, password: string) => {
      await api.register({ username, email, password });
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      setSessionExpiresAt(null);
      setSessionWarning(null);
      setState('anonymous');
      setError(null);
    }
  }, []);

  const changePassword = useCallback(
    async (input: { currentPassword: string; newPassword: string; newPasswordConfirm: string }) => {
      const result = await api.changePassword(input);
      setUser(result.user);
      setSessionExpiresAt(result.expiresAt);
      setSessionWarning(null);
    },
    [],
  );

  const updateProfile = useCallback(
    async (update: {
      displayName?: string;
      avatarUrl?: string | null;
      styleProfile?: string | null;
    }) => {
      const result = await api.updateProfile(update);
      setUser(result.user);
    },
    [],
  );

  const checkSession = useCallback(async () => {
    if (state !== 'authenticated' || !sessionExpiresAt) {
      setSessionWarning(null);
      return;
    }

    if (isRefreshingRef.current) return;
    const timeLeft = Date.parse(sessionExpiresAt) - Date.now();

    if (timeLeft <= 0) {
      handleSessionExpired();
      return;
    }

    if (timeLeft <= REFRESH_THRESHOLD_MS) {
      isRefreshingRef.current = true;
      try {
        const result = await api.refreshSession();
        setUser(result.user);
        setSessionExpiresAt(result.expiresAt);
        setSessionWarning(null);
      } catch (reason) {
        if (reason instanceof ApiClientError && reason.status === 401) {
          handleSessionExpired();
          return;
        }
        const minutesLeft = Math.max(0, Math.ceil(timeLeft / 60_000));
        setSessionWarning(`Sessie verloopt over ${minutesLeft} minuut${minutesLeft === 1 ? '' : 'en'}. Log opnieuw in om onderbreking te voorkomen.`);
      } finally {
        isRefreshingRef.current = false;
      }
      return;
    }

    if (timeLeft <= WARNING_THRESHOLD_MS) {
      const minutesLeft = Math.max(1, Math.ceil(timeLeft / 60_000));
      setSessionWarning(`Sessie verloopt over ${minutesLeft} minuut${minutesLeft === 1 ? '' : 'en'}.`);
    } else {
      setSessionWarning(null);
    }
  }, [state, sessionExpiresAt, handleSessionExpired]);

  useEffect(() => {
    if (state !== 'authenticated' || !sessionExpiresAt) {
      return undefined;
    }
    const initial = setTimeout(() => void checkSession(), 0);
    const timer = setInterval(() => void checkSession(), SESSION_CHECK_INTERVAL_MS);
    return () => {
      clearTimeout(initial);
      clearInterval(timer);
    };
  }, [state, sessionExpiresAt, checkSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      user,
      error,
      sessionWarning,
      login,
      register,
      logout,
      changePassword,
      updateProfile,
      refresh,
    }),
    [error, sessionWarning, changePassword, login, logout, refresh, register, state, updateProfile, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};